// COMANDI DA TELEFONO: stick di sterzata a sinistra, boost a destra.
//
// Il pannello e' DOM sovrapposto al canvas di Phaser, non disegnato dentro di
// esso: `position: fixed` a schermo pieno, trasparente ai tocchi (`pointerEvents:
// 'none'`) tranne che sui due comandi. Cosi' i tap sulla leaderboard, sul cash
// out e sulla rotellina continuano ad arrivare al gioco.
//
// I due comandi non parlano con Phaser direttamente: emettono `joystick_move` e
// `joystick_boost` su `window`, e MainScene li ascolta. Il vantaggio e' che
// questo file si puo' provare da solo, senza partita: basta mettersi in ascolto
// dei due eventi.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import nipplejs from 'nipplejs';

const isTouchDevice = () =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

// Sotto questa forza il tocco e' fermo al centro dello stick: li' la direzione
// non esiste (`atan2(0, 0)` vale 0, cioe' "destra"), e senza questa soglia un
// semplice tap sullo stick farebbe scattare il serpente a destra.
const FORZA_MINIMA = 0.08;

// nipplejs 1.x consegna UN solo argomento all'handler, `{ type, target, data }`.
// La 0.x ne consegnava due, `(evt, data)`. Il progetto e' passato alla 1.x senza
// accorgersene — in package.json c'era `"nipplejs": "latest"` — e il vecchio
// handler leggeva il secondo argomento, che nella 1.x e' `undefined`: la guardia
// `if (!data) return` scattava a ogni movimento e lo stick smetteva di sterzare
// **senza un errore in console**. Da qui l'accusa dal campo che "i comandi non
// rispondono".
//
// Qui accettiamo entrambe le forme, cosi' un aggiornamento non rompe di nuovo
// niente, e se non arriva nessuna delle due lo diciamo invece di tacere.
const datiDelMovimento = (evt, secondoArgomento) => {
  if (evt && evt.data && typeof evt.data === 'object') return evt.data; // 1.x
  if (secondoArgomento && typeof secondoArgomento === 'object') return secondoArgomento; // 0.x
  return null;
};

// Le opzioni stanno fuori dal componente perche' una di loro — `fadeTime` — non
// e' una scelta estetica ma una correzione, e va potuta provare senza montare
// React: vedi Joystick.test.js.
export const OPZIONI_STICK = {
  mode: 'static',
  position: { left: '50%', bottom: '50%' },
  // Due colori e non uno: passando una stringa sola, nipplejs dipinge di quel
  // colore SIA il cerchio esterno SIA il pallino, e l'esterno diventa un disco
  // verde pieno da 130px che copre l'angolo dell'arena — su un telefono in
  // orizzontale (~375px di altezza) e' un terzo dello schermo. Il fondo scuro e
  // traslucido riprende quello del pulsante BOOST.
  color: { back: 'rgba(6, 12, 6, 0.55)', front: '#39FF14' },
  size: 130,
  restOpacity: 0.6,

  // ZERO, ed e' il motivo per cui sullo schermo comparivano DUE joystick.
  //
  // In modalita' `static` nipplejs tiene un solo stick e lo ricicla. Quando il
  // dito si alza, pero', lo stick non torna subito disponibile: passa per una
  // lista di "riposo" indicizzata **per identificativo del tocco**, e ne esce
  // solo allo scadere di `fadeTime` (250ms di serie). Se un altro tocco arriva
  // prima, nipplejs lo cerca in quella lista con il NUOVO identificativo, non
  // lo trova, non trova nemmeno uno stick libero e ne **crea un secondo** — nel
  // punto dove hai appoggiato il dito, che e' perche' i due si vedevano in
  // posti diversi.
  //
  // Da mouse non succede: il puntatore ha sempre lo stesso identificativo,
  // quindi lo stick a riposo viene ritrovato e riusato. Da telefono ogni tocco
  // ne ha uno nuovo — ed e' esattamente quello che si fa giocando, staccare e
  // riappoggiare il pollice.
  //
  // A zero la restituzione e' rimandata al giro successivo del ciclo di eventi,
  // che precede sempre il prossimo tocco: la finestra si chiude del tutto.
  // La dissolvenza che si perde e' solo quella dell'opacita', ed e' rimessa in
  // App.css, dove non fa danni.
  fadeTime: 0,
};

// RETE DI SICUREZZA CONTRO LO STICK DOPPIO
//
// `fadeTime: 0` chiude la finestra in cui nipplejs ne creava un secondo (vedi
// OPZIONI_STICK), e poggia su un fatto solido: la restituzione dello stick e' un
// timer a zero, e un timer a zero scatta prima del tocco successivo, che arriva
// dopo che un dito si e' alzato e riabbassato.
//
// «Solido» non e' «garantito»: sotto carico i browser sanno dare la precedenza
// agli eventi di input rispetto ai timer in attesa, e su un telefono che disegna
// un'arena a 60 fotogrammi il carico c'e'. Se dovesse succedere, qui si rimedia:
// si toglie dallo schermo lo stick vecchio e si tiene l'ultimo, che e' quello
// appena agganciato al dito.
//
// Togliere il nodo dal DOM e' un intervento sicuro anche verso nipplejs: quando
// vorra' riusare quello stick lo rimettera' da se' (`addToDom` lo riappende se
// non lo trova nel documento). E si guarda solo il DOM, non i suoi archivi
// interni: e' gia' stata una versione di questa libreria a rompere tutto in
// silenzio, non le si da' una seconda occasione.
//
// Restituisce quanti ne ha tolti, cosi' si puo' provare.
export function potaStickDoppi(zona) {
  if (!zona) return 0;
  const stick = zona.querySelectorAll('.joystick');
  if (stick.length <= 1) return 0;

  console.error(
    `[VEXEL] nipplejs ha lasciato ${stick.length} stick sullo schermo: ne tengo ` +
    'uno solo. Se questo messaggio compare, la finestra di riuso si e\' riaperta.'
  );
  for (let i = 0; i < stick.length - 1; i += 1) stick[i].remove();
  return stick.length - 1;
}

// Aggancia la potatura alla presa dello stick.
//
// Rimandata al microtask successivo, e non e' pignoleria: nipplejs emette
// `start` PRIMA di appendere il nuovo nodo, quindi potare subito non troverebbe
// ancora il doppione. Un microtask scatta appena finito il gestore dell'evento
// e comunque prima che il browser disegni: il giocatore non fa in tempo a
// vedere due stick.
//
// Sta qui, esportata, perche' cosi' i test provano l'aggancio vero invece di
// una sua copia scritta nel test.
export function sorvegliaStickDoppi(manager, dammiZona) {
  manager.on('start', () => {
    queueMicrotask(() => potaStickDoppi(dammiZona()));
  });
}

const Joystick = () => {
  const joystickRef = useRef(null);
  const [boostAttivo, setBoostAttivo] = useState(false);

  useEffect(() => {
    if (!isTouchDevice()) return undefined;

    const manager = nipplejs.create({
      zone: joystickRef.current,
      ...OPZIONI_STICK,
    });

    // Si lamenta una volta sola: se la forma del payload cambia di nuovo, un
    // errore in console vale piu' di un comando che smette di funzionare in
    // silenzio, ma non serve ripeterlo sessanta volte al secondo.
    let formaSegnalata = false;

    // Rete di sicurezza: si controlla a ogni presa. Vedi `sorvegliaStickDoppi`.
    sorvegliaStickDoppi(manager, () => joystickRef.current);

    manager.on('move', (evt, forseDati) => {
      const dati = datiDelMovimento(evt, forseDati);

      if (!dati) {
        if (!formaSegnalata) {
          formaSegnalata = true;
          console.error(
            '[VEXEL] nipplejs: evento "move" senza dati riconoscibili. ' +
            'La libreria ha cambiato la forma del payload: lo stick non stera piu\'.',
            evt
          );
        }
        return;
      }

      // Si preferisce `vector` a `angle.radian` perche' e' l'unico campo che
      // significa la stessa cosa nella 0.x e nella 1.x: x positivo a destra,
      // y positivo verso l'ALTO. La 1.x ha cambiato l'intervallo di
      // `angle.radian` (ora 0..2π invece di -π..π), e leggerlo di la' rende il
      // codice ostaggio della prossima versione.
      const { vector, force } = dati;
      if (!vector) return;

      // Fermo al centro: nessuna direzione da comunicare, si tiene l'ultima.
      const intensita = typeof force === 'number'
        ? force
        : Math.hypot(vector.x, vector.y);
      if (intensita < FORZA_MINIMA) return;

      // Phaser misura gli angoli con la y verso il BASSO: senza il segno meno
      // il serpente andrebbe esattamente nella direzione opposta sull'asse
      // verticale. Il risultato sta in (-π, π], come quello del mouse.
      const angolo = Math.atan2(-vector.y, vector.x);

      window.dispatchEvent(new CustomEvent('joystick_move', {
        detail: { angle: angolo },
      }));
    });

    return () => {
      manager.destroy();
    };
  }, []);

  // Lo stato del boost sta anche in un ref, non solo in `useState`: serve
  // leggerlo dai listener qui sotto, che sono registrati una volta sola e non
  // vedrebbero mai il valore aggiornato.
  const boostAttivoRef = useRef(false);

  const inviaBoost = useCallback((active) => {
    if (boostAttivoRef.current === active) return; // niente eventi doppi
    boostAttivoRef.current = active;
    setBoostAttivo(active);
    window.dispatchEvent(new CustomEvent('joystick_boost', { detail: { active } }));
  }, []);

  // Rete di sicurezza per il boost acceso. `pointercancel` copre la maggior
  // parte dei casi, ma non tutti: una chiamata in arrivo o il passaggio a
  // un'altra app mettono la pagina in pausa senza cancellare il puntatore, e il
  // boost resterebbe su ON a bruciare massa mentre nessuno guarda.
  useEffect(() => {
    const spegni = () => inviaBoost(false);

    window.addEventListener('blur', spegni);
    document.addEventListener('visibilitychange', spegni);
    return () => {
      window.removeEventListener('blur', spegni);
      document.removeEventListener('visibilitychange', spegni);
      spegni(); // uscendo dalla partita il boost non deve sopravvivere al componente
    };
  }, [inviaBoost]);

  if (!isTouchDevice()) return null;

  return (
    <div style={styles.overlay}>
      {/* Zona del joystick: solo l'angolo in basso a sinistra, cosi' i tap sulla
          leaderboard, sul cash out e sulla rotellina arrivano ancora al gioco. */}
      <div ref={joystickRef} style={styles.stickZone} />

      {/* Boost: pollice destro.
          Eventi *pointer* e non *touch*, per tre motivi:
          - coprono dito, penna e mouse con un solo giro di codice;
          - `setPointerCapture` aggancia il puntatore al pulsante, quindi il
            rilascio arriva qui anche se il pollice scivola fuori dal cerchio
            mentre spinge — con `onTouchEnd` da solo il boost restava acceso;
          - React registra `touchstart` in modalita' passiva, quindi il
            `preventDefault()` che c'era prima non faceva nulla e si limitava a
            sporcare la console. Il gesto di sistema lo blocca gia'
            `touchAction: 'none'` qui sotto. */}
      <div
        style={{ ...styles.boostButton, ...(boostAttivo ? styles.boostButtonAttivo : null) }}
        onPointerDown={(e) => {
          // Prima il boost, poi la cattura, e mai il contrario:
          // `setPointerCapture` sa lanciare (`NotFoundError`) se il puntatore
          // non e' piu' attivo, e messa davanti si porterebbe via il comando.
          // E' un miglioramento, non un requisito: se fallisce, si perde solo
          // la garanzia sul rilascio col dito fuori dal cerchio.
          inviaBoost(true);
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch (err) {
            /* nessuna cattura: restano pointerup, pointercancel e blur */
          }
        }}
        onPointerUp={() => inviaBoost(false)}
        onPointerCancel={() => inviaBoost(false)}
        // Se la cattura salta a meta' gesto, il `pointerup` va all'elemento
        // sotto il dito e qui non arriva piu': il boost resterebbe acceso a
        // bruciare massa. Nel caso normale questo scatta subito DOPO il
        // `pointerup`, quindi non toglie niente a nessuno.
        onLostPointerCapture={() => inviaBoost(false)}
        onContextMenu={(e) => e.preventDefault()}
      >
        BOOST
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 1000,
    touchAction: 'none',
  },
  stickZone: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: 'min(45%, 260px)',
    height: 'min(60%, 260px)',
    pointerEvents: 'auto',
    touchAction: 'none',
  },
  boostButton: {
    position: 'absolute',
    right: 'max(18px, env(safe-area-inset-right))',
    bottom: 'max(18px, env(safe-area-inset-bottom))',
    width: '92px',
    height: '92px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: '"Space Mono", monospace',
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#39FF14',
    background: 'rgba(6, 12, 6, 0.55)',
    border: '2px solid rgba(57, 255, 20, 0.7)',
    boxShadow: '0 0 18px rgba(57, 255, 20, 0.35), inset 0 0 14px rgba(57, 255, 20, 0.12)',
    textShadow: '0 0 10px rgba(57, 255, 20, 0.8)',
    pointerEvents: 'auto',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'none',
    transition: 'background 80ms linear, box-shadow 80ms linear, transform 80ms linear',
  },
  // Il pulsante si accende sotto il dito. Non e' decorazione: e' l'unico modo
  // che ha il giocatore di distinguere "il boost non ha ricevuto il tocco" da
  // "il boost e' attivo ma non lo vedo perche' il serpente e' lento".
  boostButtonAttivo: {
    background: 'rgba(57, 255, 20, 0.30)',
    boxShadow: '0 0 28px rgba(57, 255, 20, 0.75), inset 0 0 22px rgba(57, 255, 20, 0.35)',
    transform: 'scale(0.94)',
  },
};

export default Joystick;
